import { useCallback, useRef } from "react";
import { useSetAtom } from "jotai";
import { isDirtyAtom } from "@/atoms";
import { useGlobalSavingIndicator } from "./useGlobalSavingIndicator";

export interface UseAutoSaveOptions {
  saveFunction: () => Promise<void>;
  debounceDelay?: number;
}

export const useAutoSave = ({ saveFunction, debounceDelay = 1000 }: UseAutoSaveOptions) => {
  const { withGlobalSavingIndicator } = useGlobalSavingIndicator();
  const setIsDirty = useSetAtom(isDirtyAtom);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDirtyRef = useRef<boolean>(false);
  const saveFunctionRef = useRef(saveFunction);

  // Update the ref whenever saveFunction changes
  saveFunctionRef.current = saveFunction;

  const debouncedSave = useCallback(() => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Only save if dirty
    if (isDirtyRef.current) {
      saveTimeoutRef.current = setTimeout(async () => {
        await withGlobalSavingIndicator(async () => {
          try {
            await saveFunctionRef.current();
            isDirtyRef.current = false;
            setIsDirty(false);
          } catch (error) {
            console.error("Auto-save failed:", error);
          }
        });
      }, debounceDelay);
    }
  }, [debounceDelay, withGlobalSavingIndicator, setIsDirty]);

  const markDirty = useCallback(() => {
    isDirtyRef.current = true;
    setIsDirty(true);
    debouncedSave();
  }, [debouncedSave, setIsDirty]);

  return {
    markDirty,
    debouncedSave,
  };
};
